import type {
  DailyDashboardKpi,
  DailyStockKpi,
  DayOfWeek,
  NetworkDashboardCohortUnit,
  NetworkDashboardKpis,
  NetworkDashboardLiveStoreCa,
  NetworkDashboardStoreAgeFilter,
} from '@gozoki/api-types'

import { gozokiApiWithAuth } from '../gozokiApi'

// Request an authentication token with credentials
export const fetchAllDailysKpis = async () => {
  const response = await gozokiApiWithAuth.get<DailyDashboardKpi[]>('/stores/get-all-kpis')
  return response.data
}

export const fetchAllStockKpis = async () => {
  const response = await gozokiApiWithAuth.get<DailyStockKpi[]>('/stores/get-all-stock-kpis')
  return response.data
}

export const fetchAccountingKpis = async (date: string) => {
  const response = await gozokiApiWithAuth.get(`/stores/get-accounting-kpis/${date}`) // 'AAAA-MM-DD'
  return response.data
}

export const fetchHoursData = async (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
) => {
  if (days.length === 0 || storeIds.length === 0) {
    return []
  }
  const response = await gozokiApiWithAuth.post('/stores/get-hours-data', {
    dateEnd: dateEnd.toDateString(),
    dateStart: dateStart.toDateString(),
    days,
    plage,
    storeIds,
  })
  return response.data
}

export const fetchTimeSpentData = async (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
) => {
  if (days.length === 0 || storeIds.length === 0) {
    return []
  }
  const response = await gozokiApiWithAuth.post('/stores/get-time-spent-data', {
    dateEnd: dateEnd.toDateString(),
    dateStart: dateStart.toLocaleString(),
    days,
    plage,
    storeIds,
  })
  return response.data
}

export interface FetchNetworkDashboardKpisParams {
  startDate?: string
  endDate?: string
  /**
   * Optional active-store baseline period. When set, "active stores" are computed from this window
   * instead of `startDate`/`endDate`.
   */
  activeStartDate?: string
  activeEndDate?: string
  cohortUnit?: NetworkDashboardCohortUnit
  cohortValue?: number
  /**
   * When defined (including empty string serialized), backend intersects with geographic `storeIds`
   * and skips opening-date SQL presets. Omit entirely for legacy preset-only filtering.
   */
  ageStoreIds?: number[]
}

export const fetchNetworkDashboardKpis = async (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
): Promise<NetworkDashboardKpis> => {
  const params: Record<string, string> = {}
  if (storeIds && storeIds.length > 0) params.storeIds = storeIds.join(',')
  params.storeAgeFilter = storeAgeFilter
  if (period?.startDate) params.startDate = period.startDate
  if (period?.endDate) params.endDate = period.endDate
  if (period?.activeStartDate) params.activeStartDate = period.activeStartDate
  if (period?.activeEndDate) params.activeEndDate = period.activeEndDate
  if (period?.cohortUnit) params.cohortUnit = period.cohortUnit
  if (period?.cohortValue !== undefined) params.cohortValue = String(period.cohortValue)
  if (period?.ageStoreIds !== undefined) {
    params.ageStoreIds = period.ageStoreIds.length > 0 ? period.ageStoreIds.join(',') : ''
  }
  const response = await gozokiApiWithAuth.get<NetworkDashboardKpis>(
    '/stores/get-network-dashboard-kpis',
    { params: Object.keys(params).length > 0 ? params : undefined },
  )
  return response.data
}

export const fetchNetworkDashboardLiveStoreCa = async (): Promise<NetworkDashboardLiveStoreCa> => {
  const response = await gozokiApiWithAuth.get<NetworkDashboardLiveStoreCa>(
    '/stores/get-network-dashboard-live-store-ca',
  )
  return response.data
}

export const fetchAccountingWarnings = async () => {
  const response = await gozokiApiWithAuth.get('/stores/get-accounting-warnings')
  return response.data
}

export const fetchAverageTimeSpent = async (
  currentMode: string,
  startDate: Date,
  endDate: Date,
) => {
  const response = await gozokiApiWithAuth.get(
    `/time-spent/get-average-time-spent?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&mode=${currentMode}`,
  )
  return response.data
}
